import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';
import {
  Bar,
  XAxis,
  YAxis,
  Text,
  Legend,
  Tooltip,
  BarChart,
  LabelList,
  Rectangle,
  LabelProps,
  LegendProps,
  TooltipProps,
  LegendPayload,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { Button, Spin } from 'antd';
import { useDidUpdate } from 'rooks';
import { isNil } from 'lodash';
import cx from 'classnames';

import ChartDateRangePicker, {
  DateRangeFilterType,
} from '../ChartDateRangePicker';
import {
  getDatesRange,
  getDateWithoutTime,
  getDateRangeTillLastSunday,
} from 'utils/dateUtils';
import { DateParams } from 'utils/filterUtils';
import { getFormattedNumber } from 'utils/numberUtils';
import { dateRangeDurationInWeeks } from '../utils';
import { SummaryTrendBillingItemViewModel } from 'api/backofficeApiClient';
import { getTrendWeeklyBilligProfitChartData } from 'redux/reducers/homeDashboardSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './TrendWeeklyBillingProfitChart.module.less';

enum TrendWeeklyBillingProfitBarChartTypes {
  Billing = 'billing',
  Profit = 'profit',
}

interface ChartSettings {
  activeBarIndex?: number;
  tooltipVisible: boolean;
  activeLegend?: TrendWeeklyBillingProfitBarChartTypes;
}

const pageSize = 8;

const renderYAxisTick = (props: any) => {
  const { x, y, width, height, payload } = props;

  return (
    <Text
      x={x}
      y={y}
      width={width}
      height={height}
      textAnchor="end"
      verticalAnchor="middle"
      fill="#000000"
      opacity="0.25"
    >
      {!isNil(payload.value)
        ? `$${getFormattedNumber(Math.round(payload.value))}`
        : ''}
    </Text>
  );
};

const renderTooltip = (props: TooltipProps) => {
  if (!props.active || !props.payload) return null;

  const { profitPercent } = props.payload[1].payload;

  if (isNil(profitPercent)) return '';

  return (
    <div className={styles.tooltip}>
      <span className={styles.colorBox} />
      <span>Profit: {(profitPercent * 100).toFixed(2)}%</span>
    </div>
  );
};

const renderShape = (props: any, active: boolean) => {
  const { fill, x, y, width, height, radius } = props;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      filter={active ? 'url(#trendWeeklyBillingProfitChartShadow)' : undefined}
      radius={radius}
      stroke="#000000"
      strokeWidth={active ? '0.05' : '0'}
    />
  );
};

const renderXAxisTick = (
  props: any,
  data: SummaryTrendBillingItemViewModel[],
) => {
  const { x, y, width, visibleTicksCount, height, index, payload } = props;
  const dataItem = data[index];

  return (
    <g>
      <foreignObject
        x={x - payload.offset}
        y={y}
        width={width / visibleTicksCount}
        height={height}
      >
        <div className={styles.caption}>
          {dataItem.workweekEnding ? dataItem.workweekEnding : ''}
        </div>
      </foreignObject>
    </g>
  );
};

const legendFormatter = (entry: any) => {
  // dataKey property is absent in LegendPayload typings
  switch (entry?.dataKey) {
    case TrendWeeklyBillingProfitBarChartTypes.Billing:
      return (
        <>
          <span
            className={cx(styles.legendDot, styles.legendDotBilling)}
          ></span>
          <span>Billing $</span>
        </>
      );

    case TrendWeeklyBillingProfitBarChartTypes.Profit:
      return (
        <>
          <span className={cx(styles.legendDot, styles.legendDotProfit)}></span>
          <span>Profit $</span>
        </>
      );

    default:
      return '';
  }
};

const renderBarLabel = (props: LabelProps) => {
  const { x = 0, y = 0, width = 0, height = 0, offset = 0, value } = props;

  return (
    <Text
      x={x + width / 2}
      y={y - offset}
      width={width}
      height={height}
      textAnchor="middle"
      fill="#000000"
      opacity="0.85"
    >
      {!isNil(value)
        ? `$${getFormattedNumber(Math.round(value as number))}`
        : ''}
    </Text>
  );
};

const getFormattedChartData = (
  chartData: SummaryTrendBillingItemViewModel[],
  dateFilter: DateRangeFilterType,
): SummaryTrendBillingItemViewModel[] => {
  if (!dateFilter?.startDate || !dateFilter?.endDate) return [];

  const datesRange = getDatesRange({
    startDate: dateFilter.startDate,
    endDate: dateFilter.endDate,
    step: 1,
    unit: 'week',
  });

  const formattedChartData = datesRange.map((date) => {
    const chartItem = chartData.find((chartItem) =>
      moment(date).isSame(moment(chartItem.workweekEnding), 'd'),
    );

    return {
      workweekEnding: moment(date).format('MM/DD/YYYY'),
      billing: chartItem?.billing || 0,
      profit: chartItem?.profit || 0,
      profitPercent: chartItem?.profitPercentage || 0,
    };
  });

  return formattedChartData.sort((a, b) =>
    moment(a.workweekEnding).diff(b.workweekEnding),
  );
};

export default function TrendWeeklyBillingProfitChart() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    weeklyStartDate: startDate,
    weeklyEndDate: endDate,
    ...restQueryParams
  } = queryString.parse(location.search);
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialDateFilter: DateRangeFilterType =
    startDate && endDate
      ? {
          startDate: moment(startDate, 'MM/DD/YYYY'),
          endDate: moment(endDate, 'MM/DD/YYYY'),
        }
      : defaultDateFilter;
  const [dateFilter, setDateFilter] =
    useState<DateRangeFilterType>(initialDateFilter);
  const [chartData, setChartData] = useState<
    SummaryTrendBillingItemViewModel[]
  >([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [isInitiaLoaded, setIsInitiaLoaded] = useState(false);
  const [chartSettings, setChartSettings] = useState<ChartSettings>({
    activeBarIndex: undefined,
    tooltipVisible: false,
    activeLegend: undefined,
  });
  const formattedChartData = useMemo(
    () => getFormattedChartData(chartData, dateFilter),
    [chartData, dateFilter],
  );
  const isDisabled = useMemo(
    () =>
      formattedChartData.every(
        (chartItem) => !chartItem.billing && !chartItem.profit,
      ),
    [formattedChartData],
  );

  useDidUpdate(() => {
    const queryData = {
      weeklyStartDate: dateFilter?.startDate
        ? dateFilter.startDate.format('MM/DD/YYYY')
        : undefined,
      weeklyEndDate: dateFilter?.endDate
        ? dateFilter.endDate.format('MM/DD/YYYY')
        : undefined,
      ...restQueryParams,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [dateFilter]);

  const loadTrendWeeklyBilligProfitChartData = useCallback(async () => {
    setIsChartLoading(true);

    const result = await dispatch(
      getTrendWeeklyBilligProfitChartData({
        pageSize,
        startDate: dateFilter?.startDate
          ? getDateWithoutTime(dateFilter.startDate)
          : undefined,
        endDate: dateFilter?.endDate
          ? getDateWithoutTime(dateFilter.endDate)
          : undefined,
      }),
    );

    if (getTrendWeeklyBilligProfitChartData.fulfilled.match(result)) {
      setChartData(result.payload || []);
    } else {
      setChartData([]);
    }

    setIsChartLoading(false);
    setIsInitiaLoaded(true);
  }, [dispatch, dateFilter]);

  useEffect(() => {
    loadTrendWeeklyBilligProfitChartData();
  }, [loadTrendWeeklyBilligProfitChartData]);

  const renderLegend = (props: LegendProps) => {
    const { payload } = props;

    if (!payload) return '';

    return (
      <ul className={styles.legendContainer}>
        {payload.map((entry: LegendPayload, index: number) => (
          <li
            key={`trend-billing-legend-${index}`}
            onMouseEnter={() => handleLegendMouseEnter(entry)}
            onMouseLeave={() => handleLegendMouseLeave(entry)}
          >
            {legendFormatter(entry)}
          </li>
        ))}
      </ul>
    );
  };

  const isShapeActive = (
    props: any,
    type: TrendWeeklyBillingProfitBarChartTypes,
  ) => {
    return (
      chartSettings.activeBarIndex === props.index ||
      chartSettings.activeLegend === type
    );
  };

  const handleCalendarChange = () => {
    setChartData([]);
  };

  const handleLegendMouseEnter = (event: any) => {
    if (isDisabled) return;

    const { dataKey } = event;

    setChartSettings((settings) => ({
      ...settings,
      activeLegend: dataKey,
    }));
  };

  const handleLegendMouseLeave = (event: any) => {
    setChartSettings((settings) => ({
      ...settings,
      activeLegend: undefined,
    }));
  };

  const handleBarMouseEnter = (
    type: TrendWeeklyBillingProfitBarChartTypes,
    index: number,
  ) => {
    if (isDisabled) return;

    const isTooltipVisible =
      type === TrendWeeklyBillingProfitBarChartTypes.Profit;

    setChartSettings((settings) => ({
      ...settings,
      activeBarIndex: index,
      tooltipVisible: isTooltipVisible,
    }));
  };

  const handleBarMouseLeave = () => {
    setChartSettings((settings) => ({
      ...settings,
      activeBarIndex: undefined,
      tooltipVisible: false,
    }));
  };
  return (
    <div className={cx({ [styles.disabled]: isDisabled }, styles.chartWrapper)}>
      <div className={styles.title}>
        <div>
          <Link to="/weekly-trend-billing-report-details">
            Trend - Weekly Billing & Profit
          </Link>
          <Button
            type="primary"
            onClick={() => navigate('/weekly-trend-billing-report-details')}
          >
            View Details
          </Button>
        </div>

        <div>
          <ChartDateRangePicker
            pickerKey="trendWeeklyBillingProfitFilterByDate"
            value={dateFilter}
            onChange={setDateFilter}
            onCalendarChange={handleCalendarChange}
          />
        </div>
      </div>

      <Spin spinning={isChartLoading || !isInitiaLoaded}>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={formattedChartData}
            margin={{
              top: 80,
            }}
            barSize={34}
            barGap={20}
          >
            <defs>
              <linearGradient
                id="trendWeeklyBillingBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor="#9B70E7" />
                <stop offset="1" stopColor="#C6A6FF" />
              </linearGradient>

              <linearGradient
                id="trendWeeklyProfitBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor="#BFFFDF" />
                <stop offset="1" stopColor="#85DFFF" />
              </linearGradient>

              <filter
                id="trendWeeklyBillingProfitChartShadow"
                x="0"
                y="0"
                width="150%"
                height="150%"
                filterUnits="userSpaceOnUse"
              >
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="3"
                  floodColor="#141e24"
                />
              </filter>
            </defs>

            {!isDisabled && chartSettings.tooltipVisible && (
              <Tooltip
                cursor={false}
                animationDuration={400}
                content={renderTooltip}
              />
            )}

            {isInitiaLoaded && (
              <Legend
                height={36}
                align="left"
                iconSize={0}
                verticalAlign="top"
                content={renderLegend}
                wrapperStyle={{
                  top: 20,
                }}
              />
            )}

            <CartesianGrid stroke="#f5f5f5" vertical={false} />

            <YAxis
              width={80}
              axisLine={false}
              tickLine={false}
              tick={renderYAxisTick}
              domain={[0, isDisabled ? 1000 : 'auto']}
            />

            <Bar
              minPointSize={3}
              dataKey={TrendWeeklyBillingProfitBarChartTypes.Billing}
              fill={'url(#trendWeeklyBillingBarGradient)'}
              radius={[10, 10, 0, 0]}
              shape={(props: any) =>
                renderShape(
                  props,
                  isShapeActive(
                    props,
                    TrendWeeklyBillingProfitBarChartTypes.Billing,
                  ),
                )
              }
              onMouseEnter={(data: any, index: number) =>
                handleBarMouseEnter(
                  TrendWeeklyBillingProfitBarChartTypes.Billing,
                  index,
                )
              }
              onMouseLeave={handleBarMouseLeave}
            >
              <LabelList
                position="top"
                content={renderBarLabel}
                dataKey={TrendWeeklyBillingProfitBarChartTypes.Billing}
              />
            </Bar>

            <Bar
              minPointSize={3}
              dataKey={TrendWeeklyBillingProfitBarChartTypes.Profit}
              fill={'url(#trendWeeklyProfitBarGradient)'}
              radius={[10, 10, 0, 0]}
              shape={(props: any) =>
                renderShape(
                  props,
                  isShapeActive(
                    props,
                    TrendWeeklyBillingProfitBarChartTypes.Profit,
                  ),
                )
              }
              onMouseEnter={(data: any, index: number) =>
                handleBarMouseEnter(
                  TrendWeeklyBillingProfitBarChartTypes.Profit,
                  index,
                )
              }
              onMouseLeave={handleBarMouseLeave}
            >
              <LabelList
                position="top"
                content={renderBarLabel}
                dataKey={TrendWeeklyBillingProfitBarChartTypes.Profit}
              />
            </Bar>

            <XAxis
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={(props) => renderXAxisTick(props, formattedChartData)}
            />
          </BarChart>
        </ResponsiveContainer>
      </Spin>
    </div>
  );
}
